import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DASH } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2, Plus, X, ChevronRight, AlertTriangle, Search } from "lucide-react";
import SymbolSearchDialog from "@/components/SymbolSearchDialog";
import CalibrationCoachCard from "@/components/CalibrationCoachCard";
import ProductionWelcomeBanner from "@/components/ProductionWelcomeBanner";
import ValidationBadges from "@/components/ValidationBadges";
import EvidenceCard from "@/components/EvidenceCard";
import { Ticker } from "@/components/TickerDrawer";
import { useOpenAnalysis } from "@/lib/useOpenAnalysis";

const StatCell = ({ label, value, sub, testid, tone }) => (
  <div className="grid-cell !p-4">
    <div className="label-cap">{label}</div>
    <div className={`data-lg mt-2 ${tone === "up" ? "pnl-up" : tone === "down" ? "pnl-down" : ""}`} data-testid={testid}>{value}</div>
    {sub && <div className="text-[10px] mono text-muted-foreground mt-1 uppercase tracking-wider">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [quotes, setQuotes] = useState({}); // sym -> quote
  const [names, setNames] = useState({}); // sym -> {name, exchange}
  const [searchOpen, setSearchOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState("");
  const [adding, setAdding] = useState(false);
  const nav = useNavigate();
  const openAnalysis = useOpenAnalysis(nav);

  const load = async () => {
    setLoading(true);
    try {
      const [a, p, w] = await Promise.all([
        api.get("/broker/account"),
        api.get("/positions"),
        api.get("/watchlists"),
      ]);
      setAccount(a.data);
      setPositions(p.data.filter((x) => x.status === "open"));
      setWatchlists(w.data);
      const symbols = new Set();
      w.data.forEach((wl) => wl.symbols.forEach((s) => symbols.add(s)));
      const qs = await Promise.all(
        [...symbols].map((s) => api.get(`/market/quote/${s}`).then((r) => [s, r.data]).catch(() => [s, null]))
      );
      setQuotes(Object.fromEntries(qs.filter(([, v]) => v)));
      // fire-and-forget name lookups (cached upstream)
      [...symbols].forEach((s) => {
        if (!names[s]) {
          api.get(`/market/lookup/${s}`).then((r) => {
            if (r.data?.valid) setNames((prev) => ({ ...prev, [s]: { name: r.data.name, exchange: r.data.exchange } }));
          }).catch(() => {});
        }
      });
    } catch (e) {
      if (e?.response?.status === 400) {
        toast.warning("Connect a broker in Settings to begin trading.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addSymbol = async (wid, sym) => {
    if (!sym) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/watchlists/${wid}/symbols`, { symbol: sym });
      toast.success(`Added ${data.symbol} — ${data.info?.name || ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add symbol");
    } finally { setAdding(false); }
  };

  const removeSymbol = async (wid, s) => {
    await api.delete(`/watchlists/${wid}/symbols/${s}`);
    load();
  };

  const runAnalysis = async (sym) => {
    setRunning(sym);
    try {
      await openAnalysis(sym);
    } finally { setRunning(""); }
  };

  const openPnl = positions.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);
  const equity = account?.equity || 0;
  const cash = account?.cash || 0;
  // Stage A refactor: /api/broker/account returns `provider` (mock | alpaca | none),
  // not the legacy `mode` field. Fall back through both so old cached responses
  // still work during rollout.
  const brokerMode = account?.provider || account?.mode || "disconnected";
  const isConnected = brokerMode && brokerMode !== "disconnected" && brokerMode !== "none";

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid={DASH.root}>
      <ProductionWelcomeBanner/>
      <ValidationBadges/>
      <EvidenceCard/>
      {!isConnected && (
        <div className="grid-cell border-signal-yellow/50 bg-signal-yellow/5 flex items-center gap-3" data-testid="dashboard-broker-disconnected-banner">
          <AlertTriangle className="text-signal-yellow" size={16} strokeWidth={1.75}/>
          <div className="text-sm">Broker not connected. Go to <a href="/settings" className="underline underline-offset-2 text-foreground">Settings → Broker</a> to connect Alpaca Paper or start Mock mode.</div>
        </div>
      )}

      {/* Calibration coaching — informational only, hidden unless meaningful */}
      <CalibrationCoachCard compact={false} testId="dashboard-calibration-coach"/>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border/60">        <StatCell label="Equity" value={loading ? "..." : `$${Number(equity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={brokerMode.replace("_", " ")} testid={DASH.equity}/>
        <StatCell label="Cash" value={loading ? "..." : `$${Number(cash).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub="Buying power" testid={DASH.cash}/>
        <StatCell label="Open P&L" value={loading ? "..." : (openPnl >= 0 ? "+" : "") + `$${openPnl.toFixed(2)}`} sub={`${positions.length} open`} tone={openPnl >= 0 ? "up" : "down"}/>
        <StatCell label="Open Positions" value={loading ? "..." : positions.length} sub="Long only" testid={DASH.openPositions}/>
      </div>

      {/* Watchlists + Open positions */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-border/60">
        <section className="lg:col-span-8 bg-card" data-testid={DASH.watchlist}>
          <div className="flex items-center gap-3 px-4 h-10 border-b border-border/60">
            <div className="label-cap">Watchlists</div>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                className="btn-outline h-7 !px-3 text-xs mono uppercase tracking-widest"
                onClick={()=>setSearchOpen(true)}
                data-testid={DASH.addSymbolBtn}
              ><Search size={12}/> Search & Add</button>
            </div>
          </div>

          {/* Symbol search dialog */}
          <SymbolSearchDialog
            open={searchOpen}
            onClose={()=>setSearchOpen(false)}
            onPick={(r)=>addSymbol(watchlists[0]?.id, r.symbol)}
          />

          {watchlists.map((wl) => (
            <div key={wl.id}>
              <div className="px-4 py-2 text-[11px] mono uppercase tracking-widest text-muted-foreground border-b border-border/60 bg-background/40">
                {wl.name} · {wl.user_id ? "Private" : "Shared"}
              </div>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20">
                  <tr>
                    <th className="text-left px-4 py-1.5 font-normal">Symbol</th>
                    <th className="text-left px-4 py-1.5 font-normal">Company</th>
                    <th className="text-right px-4 py-1.5 font-normal">Price</th>
                    <th className="text-right px-4 py-1.5 font-normal">Chg</th>
                    <th className="text-right px-4 py-1.5 font-normal">%Chg</th>
                    <th className="text-right px-4 py-1.5 font-normal">Src</th>
                    <th className="text-right px-4 py-1.5 font-normal"></th>
                  </tr>
                </thead>
                <tbody>
                  {wl.symbols.map((s) => {
                    const q = quotes[s] || {};
                    const info = names[s] || {};
                    const up = (q.change || 0) >= 0;
                    return (
                      <tr key={s} className="border-t border-border/40 hover:bg-secondary/30" data-testid={DASH.symbolRow(s)}>
                        <td className="px-4 py-2 mono"><Ticker symbol={s}>{s}</Ticker></td>
                        <td className="px-4 py-2 text-muted-foreground truncate max-w-[220px]" data-testid={DASH.symbolCompany(s)}>{info.name || <span className="opacity-50">—</span>}</td>
                        <td className="px-4 py-2 mono text-right">{q.price ? `$${q.price.toFixed(2)}` : "—"}</td>
                        <td className={`px-4 py-2 mono text-right ${up ? "pnl-up" : "pnl-down"}`}>{q.change ? (up ? "+" : "") + q.change.toFixed(2) : "—"}</td>
                        <td className={`px-4 py-2 mono text-right ${up ? "pnl-up" : "pnl-down"}`}>{q.change_pct != null ? (up ? "+" : "") + q.change_pct.toFixed(2) + "%" : "—"}</td>
                        <td className="px-4 py-2 text-right text-[10px] mono uppercase text-muted-foreground tracking-widest">{q.provider || "—"}</td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center gap-2 justify-end">
                            <button
                              className="btn-outline h-7 !px-2 text-[11px]"
                              onClick={() => runAnalysis(s)}
                              disabled={running === s}
                              data-testid={DASH.runAnalysis(s)}
                            >
                              {running === s ? (<><Loader2 size={12} className="animate-spin"/>Analyzing…</>) : "Run Analysis"}
                            </button>
                            <button className="btn-ghost h-7 !px-1" onClick={() => removeSymbol(wl.id, s)} title="Remove">
                              <X size={14}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {wl.symbols.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-xs text-muted-foreground">Empty watchlist — add a symbol above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ))}
        </section>

        <section className="lg:col-span-4 bg-card">
          <div className="flex items-center gap-3 px-4 h-10 border-b border-border/60">
            <div className="label-cap">Open Positions</div>
            <a href="/positions" className="ml-auto text-[10px] mono uppercase tracking-widest text-muted-foreground hover:text-foreground flex items-center gap-1">View all <ChevronRight size={12}/></a>
          </div>
          <div className="divide-y divide-border/40">
            {positions.slice(0, 8).map((p) => (
              <div key={p.id} className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="mono">{p.symbol}</div>
                  <div className="text-[10px] mono uppercase text-muted-foreground">×{p.qty}</div>
                  <div className={`ml-auto data-sm ${p.unrealized_pnl >= 0 ? "pnl-up" : "pnl-down"}`}>
                    {p.unrealized_pnl >= 0 ? "+" : ""}${p.unrealized_pnl?.toFixed(2)} <span className="text-[10px] opacity-70">({p.unrealized_pnl_pct?.toFixed(2)}%)</span>
                  </div>
                </div>
                <div className="mt-1 text-[10px] mono uppercase text-muted-foreground tracking-wider flex gap-3">
                  <span>ENT ${p.entry_price?.toFixed(2)}</span>
                  <span>STP ${p.stop_price?.toFixed(2)}</span>
                  <span>TGT ${p.target_price?.toFixed(2)}</span>
                  <span className="ml-auto">MKT ${p.current_price?.toFixed(2)}</span>
                </div>
              </div>
            ))}
            {positions.length === 0 && <div className="px-4 py-8 text-center text-xs text-muted-foreground">No open positions.</div>}
          </div>
        </section>
      </div>
    </div>
  );
}
